/**
 * Leftover Note Dialog (#728)
 *
 * Attaches or clears a foreman note on a still-unresolved transit-warehouse
 * leftover — independent of actually resolving it (keep/return). Lets a
 * foreman record context ("waiting on customer decision") before a final
 * call is made.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, TextField } from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';

interface LeftoverNoteDialogProps {
  open: boolean;
  onClose: () => void;
  partName: string;
  initialNote: string | null;
  onSave: (note: string | null) => void;
  isLoading?: boolean;
}

export function LeftoverNoteDialog({
  open,
  onClose,
  partName,
  initialNote,
  onSave,
  isLoading,
}: LeftoverNoteDialogProps) {
  const { t } = useTranslation('inventory');
  const { isDirty, markDirty } = useFormDirty(open);
  const [note, setNote] = useState(initialNote ?? '');

  useEffect(() => {
    if (open) setNote(initialNote ?? '');
  }, [open, initialNote]);

  const handleSubmit = () => {
    const trimmed = note.trim();
    onSave(trimmed.length > 0 ? trimmed : null);
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('transitWarehouse.noteDialog.title', { partName })}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      submitLabel={t('transitWarehouse.noteDialog.save')}
      isLoading={isLoading}
      maxWidth="xs"
    >
      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label={t('transitWarehouse.noteDialog.noteLabel')}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            markDirty();
          }}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
      </DialogContent>
    </FormDialog>
  );
}
